import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Search,
  ChevronDown,
  X,
  ListFilter,
  Users,
  Layers,
  Car,
  ArrowUpDown,
} from "lucide-react";
import type { ShiftTypeRow, DriverRow, ShiftRow, RosterGroupRow } from "./types";
import { loadVehicleTypes, getVehicleTypeColour, type VehicleTypeRow } from "@/lib/vehicleTypes";

/* ─── Types ─── */
export type RosterSortOption =
  | "driver_name_az"
  | "driver_name_za"
  | "start_time_asc"
  | "start_time_desc"
  | "vehicle_type_grouped";

export interface RosterFilters {
  shiftTypes: string[];
  vehicleGroups: string[];
  drivers: string[];
  sortBy: RosterSortOption;
  groupedVehicleView: string[];
}

interface RosterFilterBarProps {
  shiftTypes: ShiftTypeRow[];
  drivers: DriverRow[];
  shifts: ShiftRow[];
  rosterGroups?: RosterGroupRow[];
  filters: RosterFilters;
  onFiltersChange: (filters: RosterFilters) => void;
}

/* ─── Helpers ─── */
const stringToColour = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colours = [
    "#3B82F6",
    "#EF4444",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
    "#06B6D4",
    "#F97316",
    "#6366F1",
    "#14B8A6",
    "#E11D48",
    "#84CC16",
    "#0EA5E9",
    "#D946EF",
    "#22D3EE",
  ];

  return colours[Math.abs(hash) % colours.length];
};

const getShiftGroupValue = (shift: ShiftRow): string | null => {
  const candidate =
    (shift as any)?.schedule_group ??
    (shift as any)?.allocation_group ??
    (shift as any)?.group_name ??
    (shift as any)?.roster_group ??
    null;

  if (!candidate || typeof candidate !== "string") return null;

  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
};

// Vehicle types are canonical now (single source: vehicle_types table), so
// no more fuzzy substring bucketing needed -- the value itself is the group.
const getVehicleTypeGroup = (vehicleType?: string | null): string => {
  const raw = (vehicleType || "").trim();
  return raw || "Unassigned";
};

/* ─── Shared Dropdown Popover ─── */
const FilterDropdown: React.FC<{
  label: string;
  icon: React.ReactNode;
  items: { key: string; label: string; colour?: string; subtitle?: string }[];
  selected: string[];
  onToggle: (key: string) => void;
  onClear: () => void;
}> = ({ label, icon, items, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();

    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(q) ||
        i.key.toLowerCase().includes(q) ||
        (i.subtitle && i.subtitle.toLowerCase().includes(q))
    );
  }, [items, search]);

  const hasSelection = selected.length > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
          hasSelection
            ? "border-blue-500/60 bg-blue-500/10 text-blue-300"
            : "border-gray-600 bg-gray-800/80 text-gray-300 hover:border-gray-500 hover:text-white"
        }`}
      >
        {icon}
        <span>{label}</span>
        {hasSelection && (
          <span className="bg-blue-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {selected.length}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden">
          <div className="p-2 border-b border-gray-700/50">
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 px-1">
              {label.toUpperCase()}
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-md text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500/50"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-4">No matches</p>
            )}

            {filtered.map((item) => {
              const isSelected = selected.includes(item.key);

              return (
                <button
                  key={item.key}
                  onClick={() => onToggle(item.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? "bg-blue-500/10 text-white"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ backgroundColor: item.colour || "#6B7280" }}
                  />
                  <span className={`flex-1 truncate ${isSelected ? "font-medium" : ""}`}>
                    {item.label}
                  </span>
                  {item.subtitle && (
                    <span className="text-[10px] text-gray-500">{item.subtitle}</span>
                  )}
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {hasSelection && (
            <div className="p-2 border-t border-gray-700/50">
              <button
                onClick={() => {
                  onClear();
                  setSearch("");
                }}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Sort Dropdown ─── */
const SortDropdown: React.FC<{
  value: RosterSortOption;
  onChange: (value: RosterSortOption) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const options: { key: RosterSortOption; label: string }[] = [
    { key: "driver_name_az", label: "Driver Name (A–Z)" },
    { key: "driver_name_za", label: "Driver Name (Z–A)" },
    { key: "start_time_asc", label: "Start Time (Early–Late)" },
    { key: "start_time_desc", label: "Start Time (Late–Early)" },
    { key: "vehicle_type_grouped", label: "Vehicle Type (Grouped)" },
  ];

  const currentLabel =
    options.find((o) => o.key === value)?.label || "Sort";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800/80 text-gray-300 hover:border-gray-500 hover:text-white text-sm font-medium transition-all"
      >
        <ArrowUpDown className="h-4 w-4" />
        <span>{currentLabel}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden">
          <div className="p-2 border-b border-gray-700/50">
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-1">
              SORT
            </div>
          </div>

          <div className="py-1">
            {options.map((option) => {
              const isSelected = option.key === value;

              return (
                <button
                  key={option.key}
                  onClick={() => {
                    onChange(option.key);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? "bg-blue-500/10 text-white"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      isSelected ? "bg-blue-400" : "bg-gray-600"
                    }`}
                  />
                  <span className={`flex-1 ${isSelected ? "font-medium" : ""}`}>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Filter Bar ─── */
const RosterFilterBar: React.FC<RosterFilterBarProps> = ({
  shiftTypes,
  drivers,
  shifts,
  rosterGroups,
  filters,
  onFiltersChange,
}) => {
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  const positionItems = useMemo(() => {
    return shiftTypes.map((st) => ({
      key: st.code,
      label: `${st.code} - ${st.label}`,
      colour: st.default_colour_hex || "#6B7280",
      subtitle: st.requires_tmv ? "TMV" : st.requires_sts ? "STS" : undefined,
    }));
  }, [shiftTypes]);

  const groupItems = useMemo(() => {
    const configuredMap = new Map<string, string>();

    if (rosterGroups && rosterGroups.length > 0) {
      for (const g of rosterGroups) {
        const groupName =
          (g as any).group_name ??
          (g as any).name ??
          "";

        if ((g as any).is_active !== false && groupName) {
          configuredMap.set(groupName, (g as any).colour_hex || stringToColour(groupName));
        }
      }
    }

    const referencedGroups = new Set<string>();

    for (const driver of drivers) {
      const arr = driver.roster_groups;
      if (Array.isArray(arr)) {
        for (const name of arr) {
          if (typeof name === "string" && name.trim()) {
            referencedGroups.add(name.trim());
          }
        }
      }
    }

    for (const shift of shifts) {
      const groupValue = getShiftGroupValue(shift);
      if (groupValue) referencedGroups.add(groupValue);
    }

    const outputKeys: string[] = [];
    const seen = new Set<string>();

    if (rosterGroups && rosterGroups.length > 0) {
      const sorted = [...rosterGroups]
        .filter((g) => (g as any).is_active !== false)
        .sort((a, b) => {
          const aOrder = (a as any).sort_order ?? 9999;
          const bOrder = (b as any).sort_order ?? 9999;
          if (aOrder !== bOrder) return aOrder - bOrder;

          const aName = (a as any).group_name ?? (a as any).name ?? "";
          const bName = (b as any).group_name ?? (b as any).name ?? "";
          return aName.localeCompare(bName);
        });

      for (const g of sorted) {
        const groupName = (g as any).group_name ?? (g as any).name ?? "";
        if (groupName && !seen.has(groupName)) {
          outputKeys.push(groupName);
          seen.add(groupName);
        }
      }
    }

    const extras = Array.from(referencedGroups)
      .filter((n) => !seen.has(n))
      .sort((a, b) => a.localeCompare(b));

    for (const n of extras) {
      outputKeys.push(n);
    }

    return outputKeys.map((name) => ({
      key: name,
      label: name,
      colour: configuredMap.get(name) || stringToColour(name),
    }));
  }, [rosterGroups, drivers, shifts]);

  const driverItems = useMemo(() => {
    return drivers
      .slice()
      .sort((a, b) => a.driver_name.localeCompare(b.driver_name))
      .map((driver) => ({
        key: driver.driver_name,
        label:
          driver.driver_firstname && driver.driver_lastname
            ? `${driver.driver_firstname} ${driver.driver_lastname}`
            : driver.driver_name,
        colour: stringToColour(driver.driver_name),
        subtitle: driver.driver_name,
      }));
  }, [drivers]);

  const vehicleTypeItems = useMemo(() => {
    const set = new Set<string>();

    for (const shift of shifts) {
      set.add(getVehicleTypeGroup(shift.vehicle_type));
    }

    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((group) => ({
        key: group,
        label: group,
        colour: getVehicleTypeColour(group, vehicleTypes) || "#6B7280",
      }));
  }, [shifts, vehicleTypes]);

  const toggleArrayFilter = (
    key: "shiftTypes" | "vehicleGroups" | "drivers" | "groupedVehicleView",
    value: string
  ) => {
    const current = filters[key];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];

    onFiltersChange({
      ...filters,
      [key]: next,
    });
  };

  const clearArrayFilter = (
    key: "shiftTypes" | "vehicleGroups" | "drivers" | "groupedVehicleView"
  ) => {
    onFiltersChange({
      ...filters,
      [key]: [],
    });
  };

  const totalSelected =
    filters.shiftTypes.length +
    filters.vehicleGroups.length +
    filters.drivers.length +
    filters.groupedVehicleView.length;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-2 mr-1">
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
          <ListFilter className="h-4 w-4 text-blue-400" />
        </div>
        <div className="leading-tight">
          <p className="text-sm font-semibold text-white">Roster Filters</p>
          <p className="text-[11px] text-gray-500">
            {totalSelected > 0 ? `${totalSelected} active` : "No filters active"}
          </p>
        </div>
      </div>

      <SortDropdown
        value={filters.sortBy}
        onChange={(value) =>
          onFiltersChange({
            ...filters,
            sortBy: value,
          })
        }
      />

      <FilterDropdown
        label="Vehicle Type"
        icon={<Car className="h-4 w-4" />}
        items={vehicleTypeItems}
        selected={filters.groupedVehicleView}
        onToggle={(key) => toggleArrayFilter("groupedVehicleView", key)}
        onClear={() => clearArrayFilter("groupedVehicleView")}
      />

      <FilterDropdown
        label="Roster Groups"
        icon={<Layers className="h-4 w-4" />}
        items={groupItems}
        selected={filters.vehicleGroups}
        onToggle={(key) => toggleArrayFilter("vehicleGroups", key)}
        onClear={() => clearArrayFilter("vehicleGroups")}
      />

      <FilterDropdown
        label="Drivers"
        icon={<Users className="h-4 w-4" />}
        items={driverItems}
        selected={filters.drivers}
        onToggle={(key) => toggleArrayFilter("drivers", key)}
        onClear={() => clearArrayFilter("drivers")}
      />

      <FilterDropdown
        label="Shift Types"
        icon={<ListFilter className="h-4 w-4" />}
        items={positionItems}
        selected={filters.shiftTypes}
        onToggle={(key) => toggleArrayFilter("shiftTypes", key)}
        onClear={() => clearArrayFilter("shiftTypes")}
      />

      {totalSelected > 0 && (
        <button
          onClick={() =>
            onFiltersChange({
              shiftTypes: [],
              vehicleGroups: [],
              drivers: [],
              sortBy: "driver_name_az",
              groupedVehicleView: [],
            })
          }
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 hover:text-white hover:border-red-500/50 transition-colors text-sm"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </button>
      )}
    </div>
  );
};

export default RosterFilterBar;